import { Injectable, inject, signal, Inject, Optional } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private swUpdate = inject(SwUpdate, { optional: true });
  private configService = inject(ConfigService);

  public updateAvailable = signal(false);
  private updateCheckInterval?: number;

  constructor() {
    if (this.configService.serviceWorkerEnabled && this.swUpdate?.isEnabled) {
      this.initializeUpdateService();
    }
  }

  private initializeUpdateService(): void {
    if (!this.swUpdate) return;
    
    // Check for available updates
    this.swUpdate.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailable.set(true);
      }
    });

    // Start periodic update checks
    this.startPeriodicUpdateChecks();

    // Check for update immediately
    this.checkForUpdate();
  }

  private startPeriodicUpdateChecks(): void {
    const interval = this.configService.updateCheckInterval;
    this.updateCheckInterval = window.setInterval(() => {
      this.checkForUpdate();
    }, interval);
  }

  public checkForUpdate(): void {
    if (this.swUpdate?.isEnabled) {
      console.log('🔄 Checking for app updates...');
      this.swUpdate.checkForUpdate().then(updateFound => {
        if (updateFound) {
          console.log('✅ Update found! New version is available.');
        } else {
          console.log('ℹ️ No updates found. App is up to date.');
        }
      }).catch(err => {
        console.warn('❌ Failed to check for updates:', err);
      });
    } else {
      console.log('ℹ️ Service worker not enabled - skipping update check');
    }
  }

  public applyUpdate(): void {
    if (this.swUpdate?.isEnabled && this.updateAvailable()) {
      this.swUpdate.activateUpdate().then(() => {
        window.location.reload();
      }).catch(err => {
        console.error('Failed to apply update:', err);
      });
    }
  }

  public async forceServiceWorkerReregistration(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker not supported');
      return;
    }

    try {
      console.log('🔄 Force re-registering service worker...');
      
      // Get all registrations
      const registrations = await navigator.serviceWorker.getRegistrations();
      
      // Unregister all service workers
      await Promise.all(registrations.map(registration => {
        console.log('Unregistering service worker:', registration.scope);
        return registration.unregister();
      }));

      // Clear all caches
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => {
        console.log('Deleting cache:', cacheName);
        return caches.delete(cacheName);
      }));

      console.log('✅ Service worker and caches cleared. Please reload the page.');
      
      // Reload after a short delay to allow cleanup
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Failed to force re-register service worker:', error);
    }
  }

  public destroy(): void {
    if (this.updateCheckInterval) {
      window.clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = undefined;
    }
  }
}